import {HttpClient, HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {UrlService} from "app/core/services/url.service";
import {ErrorHandlingService} from "app/core/services/error.handling.service";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {
    BillingInfoDTO,
    CartItem,
    CartModel,
    CartUpdateModel,
    OrderStatus,
    ProductCategory,
    ProductUpdateRsp
} from "./cart.types";
import {
    getNmNdIco,
    ModuleAndResourceIDs,
    NameAndIcon
} from "../../../features/admin/components/feat-mgmt/feat-mgmt.types";
import {UrlEnum} from "../../../core/types/url-enum";
import {ObservableErrHandler} from "../../../core/types/err.handling.types";
import {AppResourceService} from "../../../features/admin/components/feat-mgmt/app-resource.service";


@Injectable({providedIn: 'root'})
export class CartService
{
    private readonly _urlService: UrlService = inject(UrlService);
    private readonly _errHandlService: ErrorHandlingService = inject(ErrorHandlingService);
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _appResourceService: AppResourceService = inject(AppResourceService);


    private readonly _cart: BehaviorSubject<CartModel> = new BehaviorSubject({
        products: [],
        enumCurrency: "RON",
        id: '',
        tenantId: '',
        totalAmount: 0,
        totalAmountWithTax: 0,
        orderStatus: OrderStatus.PENDING_PAYMENT
    });
    private readonly _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get busy$() {
        return this._busy.asObservable();
    }

    /**
     * Constructor
     */
    constructor() {

    }

    updateCartItem(cartItem:CartItem):void {
        if(this.cartHasItem(cartItem)) {
            const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
            const obs = this._httpClient.put<ProductUpdateRsp>(
                this._urlService.getUrl(UrlEnum.private_cart_update)
                    .replace("{id}", cartItem.id)
                    .replace("{count}", cartItem.count.toString()),
                null,
                {headers: headers}
            );

            const errWrapper: ObservableErrHandler = {
                observable: obs,
                err_title: "Error"
            };

            this._errHandlService.watchThis(errWrapper);
            errWrapper.observable = errWrapper.observable.pipe(tap({
                    next: response => {
                        // this._cart.value.totalAmount = response.orderPrice;
                        // const product:CartItem = this._cart.value.products.find(ci => ci.id == response.id);
                        // if(product){
                        //     product.price = response.price;
                        //     product.count = response.count;
                        // }
                        // this._cart.next(this._cart.value);
                        // this.refreshCartInternaly(this._cart.value);
                        this.refreshCart();
                    },
                    error: err => {
                        this._busy.next(false);
                    },
                    complete: () => {
                        this._busy.next(false);
                    }
                }
            ));
            errWrapper.observable.subscribe();

        }
    }

    deleteItem(cartItem:CartItem):void {
        if(this.cartHasItem(cartItem)) {
            this._busy.next(true);
            const obs = this._httpClient.delete<any>(
                this._urlService.getUrl(UrlEnum.private_cart_delete).replace('{id}', cartItem.id)
            );

            const errWrapper: ObservableErrHandler = {
                observable: obs,
                err_title: "Error"
            };

            this._errHandlService.watchThis(errWrapper);
            errWrapper.observable = errWrapper.observable.pipe(tap({
                    next: id => {
                        this.refreshCart();
                    },
                    error: err => {
                        this._busy.next(false);
                    },
                    complete: () => {
                        this._busy.next(false);
                    }
                }
            ));
            errWrapper.observable.subscribe();
        }
    }

    addItem(cartItem:CartItem):boolean {
        if(!this.cartHasItem(cartItem)) {
            this._busy.next(true);

            const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
            const updateModel:CartUpdateModel = {
                appModuleId: cartItem.appModuleId,
                appResourceId: cartItem.appResourceId,
                internalOrderId: this._cart.value.id,
                count: cartItem.count,
            }
            const obs = this._httpClient.post<string>(
                this._urlService.getUrl(UrlEnum.private_cart_add), JSON.stringify(updateModel), {headers: headers}
            );

            const errWrapper: ObservableErrHandler = {
                observable: obs,
                err_title: "Error"
            };

            this._errHandlService.watchThis(errWrapper);
            errWrapper.observable = errWrapper.observable.pipe(tap({
                    next: id => {
                        this.refreshCart();
                    },
                    error: err => {
                        this._busy.next(false);
                    },
                    complete: () => {
                        this._busy.next(false);
                    }
                }
            ));
            errWrapper.observable.subscribe();
            return true;
        }
        return false;
    }

    public checkout(billingInfo:BillingInfoDTO):boolean {
        if(this._cart?.value?.products?.length > 0) {
            if(!billingInfo){
                if(this._cart.value.paymentUrl){
                    //redirect to payment page
                    window.location.href = this._cart.value.paymentUrl;
                    return true;
                }
            }

            this._busy.next(true);
            const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
            const obs = this._httpClient.post<string>(
                this._urlService.getUrl(UrlEnum.private_cart_checkout).replace("{orderId}",this._cart.value.id), JSON.stringify(billingInfo), {headers: headers}
            );

            const errWrapper: ObservableErrHandler = {
                observable: obs,
                err_title: "Error"
            };

            this._errHandlService.watchThis(errWrapper);
            errWrapper.observable = errWrapper.observable.pipe(tap({
                    next: urlObj => {
                        window.location.href = urlObj.redirectUrl;
                        this.refreshCart();
                    },
                    error: err => {
                        this._busy.next(false);
                    },
                    complete: () => {
                        this._busy.next(false);
                    }
                }
            ));
            errWrapper.observable.subscribe();
            return true;
        }
        return false;
    }

    public cancel() {
        if (this._cart?.value?.orderStatus != OrderStatus.PENDING_PAYMENT) {
            return;
        }
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_cart_cancel).replace("{orderId}",this._cart.value.id), null, {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: urlObj => {
                    this.refreshCart();
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    getAddedSeats():number{
        const workUnitProducInCart:CartItem = this._cart.value.products.find(ci => ci.appResourceId == ModuleAndResourceIDs.WORK_UNIT);
        if(workUnitProducInCart){
            return workUnitProducInCart.count;
        }
        return 0;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<CartModel> {
        return this._cart.asObservable();
    }

    refreshCart(){
        this._busy.next(true);
        const obs = this._httpClient.get<CartModel>(
            this._urlService.getUrl(UrlEnum.private_cart_get));

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            err_message: "There was a problem getting shopping cart information."
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    this.refreshCartInternaly(value)
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    private refreshCartInternaly(cart:CartModel){
        let allProducts: CartItem[]=[];
        cart.products.forEach(ci => {
            const nmandIco: NameAndIcon = ci.productCategory == ProductCategory.RESOURCE ? getNmNdIco('RESOURCE_'+ci.resourceType) : getNmNdIco('MODULE_'+ci.internalPrefixCode);

            const prod:CartItem = {
                id: ci.id,
                count: ci.count,
                appModuleId: ci.appModuleId,
                appResourceId: ci.appResourceId,
                price: ci.price,
                enumCurrency: ci.enumCurrency,
                productCategory: ci.productCategory,
                name: nmandIco.name,
                internalOrderId: ci.internalOrderId,
                icon: nmandIco.icon,
                priceDetails: ci.priceDetails,
                resourceType: ci.resourceType,
                internalPrefixCode: ci.internalPrefixCode,
            };
            allProducts.push(prod);
        })
        allProducts.sort((a,b) => {
            return a.name.localeCompare(b.name);
        })
        cart.products = allProducts;
        this._cart.next(cart);
    }

    private cartHasItem(cartItem:CartItem):boolean{
        return (this._cart.value.products.find(ci => {
            return (cartItem.appResourceId && ci.appResourceId == cartItem.appResourceId) ||
                   (cartItem.appModuleId && ci.appModuleId == cartItem.appModuleId)
            }
        ))?.id != null;
    }

    public cartHasSubscriptionInIt():boolean{
        return this._cart.value.products.find(cartItem => cartItem.internalPrefixCode == "9") != null;
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
}
